/**
 * Client archive / permanent-delete visibility helpers.
 *
 * Archive state is stored on `archived_at` (canonical). Legacy rows may also
 * use `client_status = 'archived'`. Operational SELECT queries must exclude
 * archived clients unless a screen explicitly requests them.
 *
 * Permanent delete is allowed only when every business dependency count is
 * zero (see CLIENT_DELETE_DEPENDENCY_KEYS / cp_client_delete_dependency_snapshot).
 */

import { can, CAPABILITY, assertCan } from "./permissions";

/** Canonical dependency keys returned by cp_client_delete_dependency_snapshot. */
export const CLIENT_DELETE_DEPENDENCY_KEYS = Object.freeze([
  "projects",
  "estimates",
  "invoices",
  "time_logs",
  "active_sessions",
  "workspace_spaces",
  "workspace_engagements",
]);

export function getClientArchivePayload() {
  return {
    archived_at: new Date().toISOString(),
    client_status: "archived",
  };
}

export function getClientRestorePayload() {
  return {
    archived_at: null,
    client_status: "active",
  };
}

export function isClientArchived(client) {
  return Boolean(client?.archived_at) || client?.client_status === "archived";
}

export function getActiveClients(clients = []) {
  return (clients || []).filter((client) => !isClientArchived(client));
}

export function getArchivedClients(clients = []) {
  return (clients || []).filter((client) => isClientArchived(client));
}

/**
 * Restrict a Supabase clients query to non-archived rows.
 * Pass includeArchived: true only when a screen explicitly manages archived records.
 */
export function applyActiveClientFilter(query, { includeArchived = false } = {}) {
  if (includeArchived || !query) {
    return query;
  }

  return query.is("archived_at", null);
}

/**
 * Soft-archive a CRM client via server RPC (owner/admin only).
 * Related projects, estimates, invoices, and history are preserved.
 */
export async function archiveClient(supabaseClient, clientId, actor) {
  try {
    assertCan(actor, CAPABILITY.CLIENT_ARCHIVE);
  } catch (error) {
    return { data: null, error };
  }

  if (!supabaseClient || !clientId) {
    return { data: null, error: new Error("archiveClient: clientId is required") };
  }

  const { data, error } = await supabaseClient.rpc("cp_archive_client", {
    p_client_id: clientId,
  });

  return { data, error };
}

/**
 * Restore a soft-archived CRM client via server RPC (owner/admin only).
 */
export async function restoreClient(supabaseClient, clientId, actor) {
  try {
    assertCan(actor, CAPABILITY.CLIENT_ARCHIVE);
  } catch (error) {
    return { data: null, error };
  }

  if (!supabaseClient || !clientId) {
    return { data: null, error: new Error("restoreClient: clientId is required") };
  }

  const { data, error } = await supabaseClient.rpc("cp_restore_client", {
    p_client_id: clientId,
  });

  return { data, error };
}

export function canArchiveClients(actor) {
  return can(actor, CAPABILITY.CLIENT_ARCHIVE);
}

export function canDeleteClients(actor) {
  return can(actor, CAPABILITY.CLIENT_DELETE);
}

/**
 * Interpret server eligibility payload (or a raw dependency map).
 * UI-only helper — the RPC re-checks before deletion.
 */
export function isClientPermanentlyDeletable(eligibilityOrDependencies) {
  if (!eligibilityOrDependencies || typeof eligibilityOrDependencies !== "object") {
    return false;
  }

  if (typeof eligibilityOrDependencies.deletable === "boolean") {
    return eligibilityOrDependencies.deletable;
  }

  const deps =
    eligibilityOrDependencies.dependencies &&
    typeof eligibilityOrDependencies.dependencies === "object"
      ? eligibilityOrDependencies.dependencies
      : eligibilityOrDependencies;

  return CLIENT_DELETE_DEPENDENCY_KEYS.every((key) => Number(deps[key] || 0) === 0);
}

export function summarizeClientDeleteBlockers(eligibilityOrDependencies) {
  if (!eligibilityOrDependencies || typeof eligibilityOrDependencies !== "object") {
    return [];
  }

  const deps =
    eligibilityOrDependencies.dependencies &&
    typeof eligibilityOrDependencies.dependencies === "object"
      ? eligibilityOrDependencies.dependencies
      : eligibilityOrDependencies;

  return CLIENT_DELETE_DEPENDENCY_KEYS.filter((key) => Number(deps[key] || 0) > 0).map(
    (key) => ({ key, count: Number(deps[key]) }),
  );
}

/**
 * Load permanent-delete eligibility for a client (org-scoped RPC).
 */
export async function getClientDeleteEligibility(supabaseClient, clientId, actor) {
  try {
    assertCan(actor, CAPABILITY.CLIENT_DELETE);
  } catch (error) {
    return { data: null, error };
  }

  if (!supabaseClient || !clientId) {
    return {
      data: null,
      error: new Error("getClientDeleteEligibility: clientId is required"),
    };
  }

  const { data, error } = await supabaseClient.rpc("cp_get_client_delete_eligibility", {
    p_client_id: clientId,
  });

  return { data, error };
}

/**
 * Permanently delete an unused CRM client via server RPC (owner/admin only).
 * Server re-checks zero-dependency eligibility; rejects if anything is attached.
 */
export async function deleteClientPermanently(supabaseClient, clientId, actor) {
  try {
    assertCan(actor, CAPABILITY.CLIENT_DELETE);
  } catch (error) {
    return { data: null, error };
  }

  if (!supabaseClient || !clientId) {
    return {
      data: null,
      error: new Error("deleteClientPermanently: clientId is required"),
    };
  }

  const { data, error } = await supabaseClient.rpc("cp_delete_client", {
    p_client_id: clientId,
  });

  return { data, error };
}
